import React, { useEffect, useState } from 'react';

import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { FormFooter, FormHeader } from '@atlaskit/form';

import { Text, Stack } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { useIntl } from '@atlassian/jira-intl';

import { lookupTypeByTypeId } from '../../../common/application-types.tsx';
import ProductCard from '../common/product-card/index.tsx';
import {
	autoConfigureApplinkConsumer,
	fetchApplinkStatus,
	putApplinkOAuth2LOProvider,
	putApplinkOAuth3LOProvider,
} from '../../../rest/index.tsx';
import type { ApplicationLinkV3Status } from '../../../common/entities.tsx';
import { APPLINKS_LIST_PAGE_URL } from '../../../common/constants.tsx';
import type { StageConfirmationProps } from '../../../common/types.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
});

type ReviewSubpageProps = {
	state: StageConfirmationProps;
};

const ConfirmationSubpage = (props: ReviewSubpageProps) => {
	useIntl();

	const [status, setStatus] = useState<ApplicationLinkV3Status | null>(null);

	const configureAuth = async (confirmationApplinkId: string) => {
		// Configure OAuth
		await putApplinkOAuth3LOProvider(confirmationApplinkId);
		await autoConfigureApplinkConsumer(confirmationApplinkId);
		await putApplinkOAuth2LOProvider(confirmationApplinkId);

		setStatus(await fetchApplinkStatus(confirmationApplinkId));
	};

	useEffect(() => {
		configureAuth(props.state.confirmationApplinkId);
	}, [props.state.confirmationApplinkId]);

	const determineStatus = () => {
		if (status === null) {
			return 'not-connected';
		}

		if (status.working) {
			return 'connected';
		}
		if (status.error) {
			return 'error';
		}

		return 'not-connected';
	};

	return (
		<Form<{ username: string; password: string; remember: boolean }>
			onSubmit={() => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.href = APPLINKS_LIST_PAGE_URL;
			}}
		>
			{({ formProps }) => (
				<form {...formProps}>
					<FormHeader title="Confirmation">
						<Text>The application link has been created.</Text>
					</FormHeader>

					<Stack xcss={styles.linkDisplay} space="space.200">
						{status && (
							<ProductCard
								// TODO: Determine if the type is DC or Cloud
								applicationType={lookupTypeByTypeId(status.link.type, false)}
								displayUrl={status.link.displayUrl}
								heading="Application Link"
								connectedStatus={determineStatus()}
							/>
						)}
						{status === null && <Spinner size="medium" />}
					</Stack>

					<FormFooter>
						<ButtonGroup label="Form submit options">
							<Button type="submit" appearance="primary" isLoading={status === null}>
								Done
							</Button>
						</ButtonGroup>
					</FormFooter>
				</form>
			)}
		</Form>
	);
};

export default ConfirmationSubpage;
